import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaTrashAlt, FaTable, FaClipboardList, FaBox } from 'react-icons/fa';
import { TiDeleteOutline } from "react-icons/ti";
import { FaUserDoctor } from "react-icons/fa6";
import { MdEdit } from "react-icons/md";
import CustomModal from '../Modals/CustomModal';
import ConfirmModal from '../Modals/ConfirmModal';
import KitEntregaForm from '../Forms/KitEntregaForm';
import EncerrarGravidezForm from '../Forms/EncerrarGravidezForm';
import EditarGravidezForm from '../Forms/EditarGravidezForm';
import EditarConsultaForm from '../Forms/EditarConsultaForm';
import VincularConsultaForm from '../Forms/VincularConsultaForm';
import VincularGravidezForm from '../Forms/VincularGravidezForm';
import api from '../../services/api';
import {
  TimelineContainer,
  ActionsMenuContainer,
  MenuButton,
  MenuDropdown,
  MenuItem,
  StatusBadge,
  KitStatusBadge,
  ConsultasTable,
} from './TimelineTable.styles';

const KitNaoEntregueMotivo = {
  KIT_RECUSADO: "Kit recusado pela gestante",
  PERDA_PRAZO_ENTREGA: "Perda de prazo para entrega de kit à gestante",
  RESTRICAO_PERIODO_ELEITORAL: "Restrição de período eleitoral",
}

const TimelineTable = ({ gestante, showSuccess, showError }) => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isConsultasModalOpen, setConsultasModalOpen] = useState(false);
  const [selectedGestacao, setSelectedGestacao] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [openConsultaMenuId, setOpenConsultaMenuId] = useState(null);
  const menuRef = useRef(null);
  const consultaMenuRef = useRef(null);

  // Modal states for forms
  const [isEntregaModalOpen, setEntregaModalOpen] = useState(false);
  const [gestacaoParaEntrega, setGestacaoParaEntrega] = useState(null);
  const [isEncerrarModalOpen, setEncerrarModalOpen] = useState(false);
  const [gravidezParaEncerrar, setGravidezParaEncerrar] = useState(null);
  const [isEditarModalOpen, setEditarModalOpen] = useState(false);
  const [gravidezParaEditar, setGravidezParaEditar] = useState(null);
  const [isEditarConsultaModalOpen, setEditarConsultaModalOpen] = useState(false);
  const [consultaParaEditar, setConsultaParaEditar] = useState(null);
  const [isConfirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
  const [consultaParaExcluir, setConsultaParaExcluir] = useState(null);
  const [isAdicionarConsultaModalOpen, setAdicionarConsultaModalOpen] = useState(false);
  const [gestacaoParaAdicionarConsulta, setGestacaoParaAdicionarConsulta] = useState(null);
  const [isCriarGravidezModalOpen, setCriarGravidezModalOpen] = useState(false);
  const [isConfirmDeleteGravidezModalOpen, setConfirmDeleteGravidezModalOpen] = useState(false);
  const [gravidezParaExcluir, setGravidezParaExcluir] = useState(null);
  const [isDetalhesModalOpen, setDetalhesModalOpen] = useState(false);
  const [gestacaoParaDetalhar, setGestacaoParaDetalhar] = useState(null);
  const isGestanteInativa = gestante?.habilitado === false;

  // Fetch histórico de gestações
  const fetchHistoricoGestante = async () => {
    if (!gestante?.cpf) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/gestante/${gestante.cpf}/consultas`);
      setEvents(response.data);
    } catch (error) {
      console.error('Erro ao buscar histórico da gestante:', error);
      setEvents([]);
      showError && showError('Erro ao carregar histórico de gestações.');
    } finally {
      setLoading(false);
    }
  };

  // Load data on mount and when gestante changes
  useEffect(() => {
    fetchHistoricoGestante();
  }, [gestante?.cpf]);

  // Fecha o menu ao clicar fora dele
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
      if (consultaMenuRef.current && !consultaMenuRef.current.contains(event.target)) {
        setOpenConsultaMenuId(null);
      }
    };

    if (openMenuId !== null || openConsultaMenuId !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenuId, openConsultaMenuId]);

  // --- Event Handlers (internalized) ---
  const handleRegisterKit = (gestacao) => {
    if (isGestanteInativa) return;
    setGestacaoParaEntrega(gestacao);
    setEntregaModalOpen(true);
    setOpenMenuId(null);
  };


  const handleEncerrarGravidez = (gravidez) => {
    if (isGestanteInativa) return;
    setGravidezParaEncerrar({ ...gravidez, gestante_cpf: gestante?.cpf });
    setEncerrarModalOpen(true);
    setOpenMenuId(null);
  };

  const handleEditarGravidez = (gravidez) => {
    if (isGestanteInativa) return;
    setGravidezParaEditar({ ...gravidez, gestante_cpf: gestante?.cpf });
    setEditarModalOpen(true);
    setOpenMenuId(null);
  };

  const handleAdicionarConsulta = (gestacao) => {
    if (isGestanteInativa) return;
    setGestacaoParaAdicionarConsulta(gestacao);
    setAdicionarConsultaModalOpen(true);
    setOpenMenuId(null);
  };

  const handleExcluirGravidez = (gravidez) => {
    if (isGestanteInativa) return;
    setGravidezParaExcluir(gravidez);
    setConfirmDeleteGravidezModalOpen(true);
    setOpenMenuId(null);
  };

    const handleHistoricoGravidez = (gravidez) => {
    navigate(`/gestante/historico-vacina/${gestante.cpf}`);
    setOpenMenuId(null);
  };

  const handleDetalharGestacao = (gestacao) => {
    setGestacaoParaDetalhar(gestacao);
    setDetalhesModalOpen(true);
    setOpenMenuId(null);
  };

  const handleEditarConsulta = (gravidezId, consulta, dataFim = null) => {
    if (isGestanteInativa) return;
    setConsultaParaEditar({ ...consulta, gravidezId, dataFim });
    setEditarConsultaModalOpen(true);
    setOpenConsultaMenuId(null);
    setConsultasModalOpen(false);
  };

  const handleExcluirConsulta = (gravidezId, consulta) => {
    if (isGestanteInativa) return;
    setConsultaParaExcluir({ gravidezId, consulta });
    setConfirmDeleteModalOpen(true);
    setOpenConsultaMenuId(null);
    setConsultasModalOpen(false);
  };

  const confirmarExclusaoConsulta = async () => {
    if (!consultaParaExcluir || !gestante?.cpf) return;
    try {
      await api.delete(
        `/gestante/${gestante.cpf}/gravidez/${consultaParaExcluir.gravidezId}/consulta/${consultaParaExcluir.consulta.id}`
      );
      fetchHistoricoGestante();
      showSuccess && showSuccess('Consulta excluída com sucesso!');
    } catch (error) {
      console.error("Erro ao excluir consulta:", error);
      showError && showError('Erro ao excluir consulta. Tente novamente.');
    } finally {
      setConsultaParaExcluir(null);
    }
  };

  const confirmarExclusaoGravidez = async () => {
    if (isGestanteInativa) return;
    if (!gravidezParaExcluir || !gestante?.cpf) return;
    try {
      await api.delete(`/gestante/${gestante.cpf}/gravidez/${gravidezParaExcluir.id}`);
      fetchHistoricoGestante();
      showSuccess && showSuccess('Gestação excluída com sucesso!');
    } catch (error) {
      console.error("Erro ao excluir gestação:", error);
      showError && showError('Erro ao excluir gestação. Tente novamente.');
    } finally {
      setGravidezParaExcluir(null);
    }
  };

  const toggleMenu = (eventoId) => {
    setOpenMenuId(openMenuId === eventoId ? null : eventoId);
  };

  const toggleConsultaMenu = (consultaId) => {
    setOpenConsultaMenuId(openConsultaMenuId === consultaId ? null : consultaId);
  };

  const handleVerConsultas = (gestacao) => {
    setSelectedGestacao(gestacao);
    setConsultasModalOpen(true);
    setOpenMenuId(null);
  };

  // Função para determinar a classe do badge de status de gestação
  const getStatusGestacaoClass = (status) => {
    if (status === "Em Andamento") return "em-andamento";
    if (status === "Encerrada") return "encerrada";
    if (status === "Interrompida") return "interrompida";
    return "";
  };

  // Função para determinar a classe do badge de status de kit
  const getStatusKitClass = (status) => {
    if (status === "Entregue") return "entregue";
    if (status === "Não entregue") return "nao-entregue";
    return "pendente";
  };
  const hasGestacaoActive = events.some(e => e.motivo_encerramento === null && e.status_gravidez !== "Encerrada")
  const calcularSemanasGestacao = (dum, dataFim) =>{
    const [dia, mes, ano] = dum.split("/").map(Number)
    const dataDUM = new Date(ano, mes -1, dia);
    
    let  hoje = new Date();
    if(dataFim){
      const [dia, mes, ano] = dataFim?.split("/").map(Number)
      hoje = new Date(ano, mes -1, dia);
    }
    const diffMs = hoje - dataDUM;

    const dias = diffMs / (1000 * 60 * 60 * 24)

    const semanas = Math.floor(dias / 7);
    const diasRestantes = Math.floor(dias % 7)

    return `${semanas} semanas e ${diasRestantes} dia(s)`
  }
  return (
    <TimelineContainer>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h4 style={{ margin: 0 }}>Histórico de Gestações</h4>
        {!hasGestacaoActive && !loading && events && events.length > 0 && (
          <button
            disabled={isGestanteInativa || hasGestacaoActive}
            onClick={() => {
              if (isGestanteInativa) return;
              setCriarGravidezModalOpen(true);
            }}
            style={{
              padding: '8px 16px',
              backgroundColor: isGestanteInativa ? '#b5b5b5' : '#595959',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isGestanteInativa || hasGestacaoActive ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'background-color 0.2s',
              opacity: isGestanteInativa || hasGestacaoActive ? 0.6 : 1,
            }}
            onMouseOver={(e) => {
              if (isGestanteInativa || hasGestacaoActive) return;
              e.target.style.backgroundColor = '#404040';
            }}
            onMouseOut={(e) => {
              if (isGestanteInativa || hasGestacaoActive) return;
              e.target.style.backgroundColor = '#595959';
            }}
          >
            + Nova Gestação
          </button>
        )}
      </div>
      {loading ? (
        <p>Carregando histórico...</p>
      ) : events && events.length > 0 ? (
        <table>
          <thead>
            <tr>
              <th>DUM</th>
              <th>Qtd de semanas de gestação</th>
              <th>Status da Gestação</th>
              <th>Status de Entrega do Kit</th>
              <th>Qtd de Filhos da Gestação</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {events.map((evento, index) => {
              const motivoNaoEntregaKit = evento.motivo_nao_entrega_kit || null;
              return (
                <tr key={index}>
                  <td>{evento.data}</td>
                  <td>{calcularSemanasGestacao(evento.data, evento?.data_fim)}</td>
                  <td>
                    <StatusBadge className={getStatusGestacaoClass(evento.status_gravidez)}>
                      {evento.status_gravidez}
                    </StatusBadge>
                  </td>
                  <td>
                    <KitStatusBadge className={getStatusKitClass(evento.status_kit)} title={evento?.data_nao_entrega_kit || evento?.data_entrega_kits}>
                      {motivoNaoEntregaKit === null ? evento.status_kit : KitNaoEntregueMotivo[motivoNaoEntregaKit]}
                    </KitStatusBadge>
                  </td>
                  <td>{evento.qtd_filhos}</td>
                  <td>
                    <ActionsMenuContainer ref={openMenuId === evento.id ? menuRef : null}>
                      <MenuButton onClick={() => toggleMenu(evento.id)}>
                        ⋮
                      </MenuButton>
                      {openMenuId === evento.id && (
                        <MenuDropdown>
                          <MenuItem
                            onClick={() => handleDetalharGestacao(evento)}
                          >
                            <FaClipboardList style={{ marginRight: '8px' }} /> Detalhar Gestação
                          </MenuItem>
                          {evento.consultas && evento.consultas.length > 0 ? (
                            <>
                              <MenuItem
                                className="primary"
                                onClick={() => handleVerConsultas(evento)}
                              >
                                <FaUserDoctor style={{ marginRight: '8px' }} /> Histórico de Consultas ({evento.consultas.length})
                              </MenuItem>
                              <MenuItem
                                onClick={() => handleAdicionarConsulta(evento)}
                                disabled={isGestanteInativa}
                              >
                                <FaPlus style={{ marginRight: '8px' }} /> Adicionar Consulta
                              </MenuItem>

                            </>

                          ) : (
                            <MenuItem
                              className="primary"
                              onClick={() => handleAdicionarConsulta(evento)}
                              disabled={isGestanteInativa}
                            >
                              <FaPlus style={{ marginRight: '8px' }} /> Adicionar Consulta
                            </MenuItem>
                          )}

                          {evento.status_kit !== "Entregue" && motivoNaoEntregaKit === null && (
                            <MenuItem
                              className="success"
                              onClick={() => handleRegisterKit(evento)}
                              disabled={isGestanteInativa}
                            >
                              <FaBox style={{ marginRight: '8px' }} /> Registrar Entrega de Kit
                            </MenuItem>
                          )}

                          {evento.status_gravidez === "Em Andamento" && (
                            <>
                              <MenuItem
                                className="war"
                                  onClick={() => handleEncerrarGravidez(evento)}
                                  disabled={isGestanteInativa}
                              >
                                <TiDeleteOutline style={{ marginRight: '8px' }} />  Encerrar Gestação
                              </MenuItem>
                              <MenuItem
                                  onClick={() => handleEditarGravidez(evento)}
                                  disabled={isGestanteInativa}
                              >
                                <MdEdit style={{ marginRight: '8px' }} />  Editar Gestação
                              </MenuItem>
                              <MenuItem
                                className="danger"
                                  onClick={() => handleExcluirGravidez(evento)}
                                  disabled={isGestanteInativa}
                              >
                                <FaTrashAlt style={{ marginRight: '8px' }} />  Excluir Gestação
                              </MenuItem>                             
                            </>
                          )}
                          <MenuItem
                            className="primary"
                            onClick={() => handleHistoricoGravidez(evento)}
                          >
                            <FaTable style={{ marginRight: '8px' }} />  Histórico de Vacinação
                          </MenuItem>                           
                        </MenuDropdown>
                      )}
                    </ActionsMenuContainer>
                  </td>
                </tr>
              )
            })}

          </tbody>

        </table>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <p style={{ marginBottom: '20px', color: '#666' }}>Não há histórico de gestações registrado.</p>
          <button
            disabled={isGestanteInativa}
            onClick={() => {
              if (isGestanteInativa) return;
              setCriarGravidezModalOpen(true);
            }}
            style={{
              padding: '12px 24px',
              backgroundColor: isGestanteInativa ? '#b5b5b5' : '#595959',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isGestanteInativa ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              fontWeight: '500',
              transition: 'background-color 0.2s',
              opacity: isGestanteInativa ? 0.6 : 1
            }}
            onMouseOver={(e) => {
              if (isGestanteInativa) return;
              e.target.style.backgroundColor = '#404040';
            }}
            onMouseOut={(e) => {
              if (isGestanteInativa) return;
              e.target.style.backgroundColor = '#595959';
            }}
          >
            + Cadastrar Nova Gestação
          </button>
        </div>
      )}

      {/* Modal de Consultas */}
      <CustomModal
        isOpen={isConsultasModalOpen}
        onClose={() => setConsultasModalOpen(false)}
        title="Histórico de Consultas"
      >
        {selectedGestacao && (
          <div>
            <p style={{ marginBottom: '15px', color: '#666' }}>
              <strong>Gestação iniciada em:</strong> {selectedGestacao.data}
            </p>
            {selectedGestacao.consultas && selectedGestacao.consultas.length > 0 ? (
              <ConsultasTable>
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Profissional</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedGestacao.consultas.map((consulta, idx) => (
                    <tr key={idx}>
                      <td>{consulta.data}</td>
                      <td>{consulta.profissional}</td>
                      <td>
                        <ActionsMenuContainer ref={openConsultaMenuId === consulta.id ? consultaMenuRef : null}>
                          <MenuButton onClick={() => toggleConsultaMenu(consulta.id)}>
                            ⋮
                          </MenuButton>
                          {openConsultaMenuId === consulta.id && (
                            <MenuDropdown>
                              <MenuItem
                                onClick={() => handleEditarConsulta(selectedGestacao.id, consulta, selectedGestacao.data_fim)}
                                disabled={isGestanteInativa}
                              >
                                <MdEdit style={{ marginRight: '8px' }} />  Editar Consulta
                              </MenuItem>
                              <MenuItem
                                className="danger"
                                onClick={() => handleExcluirConsulta(selectedGestacao.id, consulta)}
                                disabled={isGestanteInativa}
                              >
                                <FaTrashAlt style={{ marginRight: '8px' }} />   Excluir Consulta
                              </MenuItem>
                            </MenuDropdown>
                          )}
                        </ActionsMenuContainer>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </ConsultasTable>
            ) : (
              <p style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
                Sem consultas registradas para esta gestação.
              </p>
            )}
          </div>
        )}
      </CustomModal>

      {/* Modal para registrar a entrega de kits */}
      <CustomModal
        isOpen={isEntregaModalOpen}
        onClose={() => setEntregaModalOpen(false)}
        title="Registrar Entrega de Kit"
      >
        {gestacaoParaEntrega && gestante && (
          <KitEntregaForm
            gestacao={gestacaoParaEntrega}
            gestante={gestante}
            onClose={() => setEntregaModalOpen(false)}
            onEntregaRegistrada={(message) => {
              fetchHistoricoGestante();
              showSuccess && showSuccess(message ?? 'Kit entregue com sucesso!');
            }}
          />
        )}
      </CustomModal>

      {/* Modal para Encerrar Gravidez */}
      <CustomModal
        isOpen={isEncerrarModalOpen}
        onClose={() => setEncerrarModalOpen(false)}
        title="Encerrar Gestação"
      >
        {gravidezParaEncerrar && (
          <EncerrarGravidezForm
            gravidez={gravidezParaEncerrar}
            onClose={() => {
              setEncerrarModalOpen(false);
              fetchHistoricoGestante();
            }}
          />
        )}
      </CustomModal>

      {/* Modal para Editar Gravidez */}
      <CustomModal
        isOpen={isEditarModalOpen}
        onClose={() => setEditarModalOpen(false)}
        title="Editar Gestação"
      >
        {gravidezParaEditar && (
          <EditarGravidezForm
            gravidez={gravidezParaEditar}
            onClose={() => {
              setEditarModalOpen(false);
              fetchHistoricoGestante();
            }}
          />
        )}
      </CustomModal>

      {/* Modal para Editar Consulta */}
      <CustomModal
        isOpen={isEditarConsultaModalOpen}
        onClose={() => setEditarConsultaModalOpen(false)}
        title="Editar Consulta"
      >
        {consultaParaEditar && (
          <EditarConsultaForm
            consulta={consultaParaEditar}
            gravidezId={consultaParaEditar.gravidezId}
            dataFim={consultaParaEditar.dataFim}
            onClose={() => setEditarConsultaModalOpen(false)}
            onAtualizar={() => {
              fetchHistoricoGestante();
              showSuccess && showSuccess('Consulta atualizada com sucesso!');
            }}
          />
        )}
      </CustomModal>

      {/* Modal para Adicionar Consulta */}
      <CustomModal
        isOpen={isAdicionarConsultaModalOpen}
        onClose={() => {
          setAdicionarConsultaModalOpen(false);
          setGestacaoParaAdicionarConsulta(null);
        }}
        title="Adicionar Consulta"
      >
        {gestacaoParaAdicionarConsulta && gestante && (
          <VincularConsultaForm
            gestanteCpf={gestante.cpf}
            gravidezId={gestacaoParaAdicionarConsulta.id}
            onClose={() => {
              setAdicionarConsultaModalOpen(false);
              setGestacaoParaAdicionarConsulta(null);
            }}
            onConsultaVinculada={() => {
              fetchHistoricoGestante();
              showSuccess && showSuccess('Consulta adicionada com sucesso!');
              setAdicionarConsultaModalOpen(false);
              setGestacaoParaAdicionarConsulta(null);
            }}
          />
        )}
      </CustomModal>

      {/* Modal de confirmação para excluir consulta */}
      <ConfirmModal
        isOpen={isConfirmDeleteModalOpen}
        onClose={() => {
          setConfirmDeleteModalOpen(false);
          setConsultaParaExcluir(null);
        }}
        onConfirm={confirmarExclusaoConsulta}
        title="Confirmar Exclusão"
        message={
          consultaParaExcluir
            ? `Tem certeza que deseja excluir a consulta do dia ${consultaParaExcluir.consulta.data}?`
            : ''
        }
        confirmText="Excluir"
        confirmButtonColor="#1a1a1a"
      />
      
      <ConfirmModal
        isOpen={isConfirmDeleteGravidezModalOpen}
        onClose={() => {
          setConfirmDeleteGravidezModalOpen(false);
          setGravidezParaExcluir(null);
        }}
        onConfirm={confirmarExclusaoGravidez}
        title="Excluir Gestação"
        message={
          gravidezParaExcluir
            ? `Você tem certeza que deseja excluir esta gestação? Esta ação é irreversível.`
            : ''
        }
        confirmText="Excluir"
        confirmButtonColor="#1a1a1a"
      />

      {/* Modal de Detalhes da Gestação */}
      <CustomModal
        isOpen={isDetalhesModalOpen}
        onClose={() => setDetalhesModalOpen(false)}
        title="Detalhes da Gestação"
      >
        {gestacaoParaDetalhar && (
          <div style={{ padding: '10px 0' }}>
            <div style={{ marginBottom: '15px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                <strong style={{ color: '#555' }}>DUM:</strong>
                <span>{gestacaoParaDetalhar.data}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                <strong style={{ color: '#555' }}>Qtd de semanas de gestação:</strong>
                <span>{calcularSemanasGestacao(gestacaoParaDetalhar.data, gestacaoParaDetalhar?.data_fim)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                <strong style={{ color: '#555' }}>Status da Gestação:</strong>
                <span>
                  <StatusBadge className={getStatusGestacaoClass(gestacaoParaDetalhar.status_gravidez)}>
                    {gestacaoParaDetalhar.status_gravidez}
                  </StatusBadge>
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                <strong style={{ color: '#555' }}>Status da Entrega do Kit:</strong>
                <span>
                  <KitStatusBadge className={getStatusKitClass(gestacaoParaDetalhar.status_kit)}>
                    {gestacaoParaDetalhar.status_kit}
                  </KitStatusBadge>
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                <strong style={{ color: '#555' }}>Qtd de Filhos da Gestação:</strong>
                <span>{gestacaoParaDetalhar.qtd_filhos}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                <strong style={{ color: '#555' }}>Data de Encerramento da Gestação:</strong>
                <span>{gestacaoParaDetalhar.data_fim || '-'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                <strong style={{ color: '#555' }}>Motivo de Encerramento:</strong>
                <span>{gestacaoParaDetalhar.motivo_encerramento || '-'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                <strong style={{ color: '#555' }}>Data da Entrega:</strong>
                <span>{gestacaoParaDetalhar.data_entrega_kits || '-'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                <strong style={{ color: '#555' }}>Quantidade de Kits Entregues:</strong>
                <span>{gestacaoParaDetalhar.qtd_kits || '-'}</span>
              </div>
              {gestacaoParaDetalhar.justificativa_kits_menor_qtd_filhos && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', padding: '8px 0' }}>
                  <strong style={{ color: '#555' }}>Justificativa de kits entregues menor que a quantidade de filhos:</strong>
                  <span>{gestacaoParaDetalhar.justificativa_kits_menor_qtd_filhos}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </CustomModal>

      {/* Modal para Criar Nova Gravidez */}
      <CustomModal
        isOpen={isCriarGravidezModalOpen}
        onClose={() => setCriarGravidezModalOpen(false)}
        title="Cadastrar Nova Gestação"
      >
        {gestante && (
          <VincularGravidezForm
            gestante={gestante}
            gestacao={events || []}
            onClose={() => setCriarGravidezModalOpen(false)}
            onGravidezVinculada={() => {
              fetchHistoricoGestante();
              showSuccess && showSuccess('Gestação cadastrada com sucesso!');
              setCriarGravidezModalOpen(false);
            }}
          />
        )}
      </CustomModal>
    </TimelineContainer>
  );
};

TimelineTable.propTypes = {
  gestante: PropTypes.object.isRequired,
  showSuccess: PropTypes.func,
  showError: PropTypes.func,
};

export default TimelineTable;
